import { ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useLocale, useRtl } from 'vuetify';

import baseLocales from '@/shared/assets/locale.json';
import useApi from '@/shared/composables/useApi';
import useAssets from '@/shared/composables/useAssets';

// Module-level state shared across all instances
const fetchedLocales = ref(['en']);

/**
 * Composable for loading translations and switching locales.
 * Ensures translations are loaded BEFORE the locale is set,
 * preventing race conditions where UI renders with fallback text.
 */
export default function useLocaleLoader() {
  const { api } = useApi();
  const { assets } = useAssets();
  const i18n = useI18n();
  const vuetifyLocale = useLocale();
  const vuetifyRtl = useRtl();

  /**
   * Replace Liquid template syntax with i18n-safe placeholders.
   * Converts {{ }} to -% %- to avoid conflicts with vue-i18n.
   */
  function replaceLiquid(messagesWithLiquid) {
    if (!messagesWithLiquid) return {};
    const messages = { ...messagesWithLiquid };
    Object.keys(messages).forEach((key) => {
      const replacedKey = key.replace(/{{/g, '-%').replace(/}}/g, '%-');
      const replacedValue = messages[key].replace(/{{/g, '-%').replace(/}}/g, '%-');
      messages[replacedKey] = replacedValue;
    });
    return messages;
  }

  /**
   * Load translations for a locale without changing the active locale.
   * Fetches from API and merges with base locale translations.
   */
  async function loadTranslations(locale) {
    if (locale === 'en' || fetchedLocales.value.includes(locale)) {
      return;
    }

    const translationKeyValue = await api.translations.index(locale);
    const messagesWithLiquidReplacement = replaceLiquid(translationKeyValue);
    const baseMessagesWithLiquidReplacement = replaceLiquid(baseLocales[locale]);

    i18n.setLocaleMessage(locale, {
      ...baseMessagesWithLiquidReplacement,
      ...messagesWithLiquidReplacement,
    });

    fetchedLocales.value.push(locale);
  }

  /**
   * Set the active locale and update related settings (Vuetify, RTL, HTML lang).
   * Call loadTranslations() first if translations haven't been loaded.
   */
  function setLocale(locale) {
    i18n.locale.value = locale;

    // Vuetify uses 'zhHant' for traditional Chinese, we use 'zhcht'
    if (locale === 'zhcht') {
      vuetifyLocale.current.value = 'zhHant';
    } else {
      vuetifyLocale.current.value = locale;
    }

    vuetifyRtl.value = assets.rightToLeftLanguages.includes(locale);
    document.documentElement.lang = locale;
    window.locale = locale;
  }

  /**
   * Load translations then set the locale.
   * This is the primary method to use when switching locales.
   */
  async function switchLocale(locale) {
    await loadTranslations(locale);
    setLocale(locale);
  }

  return {
    fetchedLocales,
    switchLocale,
  };
}
